/**
 * UniGate credentials form — flat 2-column house modal (black header, ruby
 * underline, ruby icon tile, white body, black primary button). WRITE-ONLY:
 * every field starts empty; nothing stored is ever shown. Fits 1366×768.
 * Used for the platform default and a location's own account.
 */
import React, { useState } from 'react';
import { X, KeyRound, MapPin } from 'lucide-react';
import { Segmented } from '../cardSetup/ReaderPicker';
import { RubyGemMark } from '../../RubyGemMark';
import { type CredForm, type TeconnectData, type TeconnectForm, emptyCredForm, withEnvironment, credProblems, credBody, emptyTeconnectForm, teconnectFilled, teconnectProblem, teconnectBody } from './unigateCreds';

/** What onSave receives: UniGate fields, a `teconnect` block, or both (the server keeps whatever is omitted). */
export type CredSaveBody = Partial<ReturnType<typeof credBody>> & { teconnect?: TeconnectData };

const RUBY_UNDERLINE = { background: 'linear-gradient(90deg, #e8365a, #DC143C, #9F1239)', height: '3px' };
const INPUT = 'min-h-[44px] w-full rounded-lg border border-stone-300 bg-white px-3 text-sm text-stone-900 outline-none placeholder:text-stone-400 focus:border-ruby focus:ring-2 focus:ring-rose-200';

const Field: React.FC<{ label: string; error?: string | null; hint?: string; children: React.ReactElement }> = ({ label, error, hint, children }) => (
  <div className="block">
    <span className="mb-1 block text-xs font-medium text-stone-700">{label}</span>
    {children.type === 'input' ? React.cloneElement(children as React.ReactElement<any>, { 'aria-label': label }) : children}
    {error ? <span className="mt-0.5 block text-[11px] text-ruby-deep">{error}</span>
      : hint ? <span className="mt-0.5 block text-[11px] text-stone-500">{hint}</span> : null}
  </div>
);

interface Props {
  title: string;
  /** A chip naming the scope ("Platform default" / the location name). */
  scope: string;
  scopeIsLocation?: boolean;
  /** Save the body; throw an api error to show it. */
  onSave: (body: CredSaveBody) => Promise<void>;
  onClose: () => void;
  /** Show the "Website (TEConnect)" section, prefilled from `teconnect` (public data, unlike UniGate). */
  withTeconnect?: boolean;
  teconnect?: TeconnectData | null;
  /** Hint under the website section (e.g. when it only applies to an own-credentials location). */
  teconnectHint?: string;
}

export const UnigateCredentialsModal: React.FC<Props> = ({ title, scope, scopeIsLocation, onSave, onClose, withTeconnect, teconnect, teconnectHint }) => {
  const [f, setF] = useState<CredForm>(emptyCredForm);
  const [te, setTe] = useState<TeconnectForm>(() => emptyTeconnectForm(teconnect));
  const setT = (k: keyof TeconnectForm, v: string) => { setTe((x) => ({ ...x, [k]: v })); setError(null); };
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** The server said the host is not magensa.net — ask before sending confirmHost. */
  const [hostToConfirm, setHostToConfirm] = useState<string | null>(null);
  const set = (k: keyof CredForm, v: string) => { setF((x) => ({ ...x, [k]: v })); setTouched((t) => ({ ...t, [k]: true })); setHostToConfirm(null); };
  const problems = credProblems(f);
  const show = (k: keyof CredForm) => (touched[k] ? problems[k] || null : null);
  const uniOk = Object.keys(problems).length === 0;
  const uniEmpty = !f.customerCode.trim() && !f.username.trim() && !f.password;
  const teFilled = !!withTeconnect && teconnectFilled(te);
  const teProblem = withTeconnect ? teconnectProblem(te) : null;
  // UniGate fields are required together; with the website section, leaving them ALL empty saves just the website key.
  const canSave = withTeconnect
    ? !teProblem && ((uniOk) || (uniEmpty && teFilled))
    : uniOk;

  const submit = async (confirmHost = false) => {
    setBusy(true); setError(null);
    try {
      const te_ = teFilled ? { teconnect: teconnectBody(te, teconnect) } : {};
      await onSave(uniOk ? { ...credBody(f, confirmHost), ...te_ } : { ...te_ });
      onClose();
    } catch (e: any) {
      if (e?.code === 'UNKNOWN_UNIGATE_HOST' && !confirmHost) {
        let host = f.baseUrl; try { host = new URL(f.baseUrl).hostname; } catch { /* keep text */ }
        setHostToConfirm(host);
      } else {
        setError(e?.serverMessage || e?.message || 'The credentials were not saved.');
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" role="dialog" aria-modal="true" aria-label={title}>
      <div className="w-full max-w-[760px] overflow-hidden rounded-2xl bg-white shadow-2xl" data-testid="unigate-modal">
        <header className="flex items-center gap-3 bg-black px-5 py-3.5 text-white">
          <span className="grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-ruby text-white"><KeyRound size={18} /></span>
          <div className="min-w-0 flex-1">
            <h2 className="m-0 text-[15px] font-semibold">{title}</h2>
            <div className="truncate text-xs text-stone-400">MagTek · Magensa UniGate</div>
          </div>
          <span className="inline-flex items-center gap-1 rounded-full border border-stone-700 px-2.5 py-1 text-[11px] text-stone-300">
            {scopeIsLocation && <MapPin size={11} />} {scope}
          </span>
          <button type="button" aria-label="Close" onClick={onClose} className="grid h-11 w-11 place-items-center rounded-lg text-stone-400 hover:bg-white/10 hover:text-white"><X size={18} /></button>
        </header>
        <div style={RUBY_UNDERLINE} />
        <div className="grid grid-cols-1 gap-4 p-5 md:grid-cols-2">
          <div className="space-y-3">
            <Field label="Customer code" error={show('customerCode')}>
              <input className={`${INPUT} font-mono`} value={f.customerCode} onChange={(e) => set('customerCode', e.target.value)} autoComplete="off" data-testid="ug-code" />
            </Field>
            <Field label="Username" error={show('username')}>
              <input className={`${INPUT} font-mono`} value={f.username} onChange={(e) => set('username', e.target.value)} autoComplete="off" data-testid="ug-user" />
            </Field>
            <Field label="Password" error={show('password')} hint="Write-only — never shown again after saving.">
              <input className={`${INPUT} font-mono`} type="password" value={f.password} onChange={(e) => set('password', e.target.value)} autoComplete="new-password" data-testid="ug-pass" />
            </Field>
          </div>
          <div className="space-y-3">
            <Field label="Environment">
              <Segmented ariaLabel="Environment" value={f.environment}
                options={[{ id: 'pilot', label: 'Pilot' }, { id: 'live', label: 'Live' }]}
                onChange={(v) => { setF((x) => withEnvironment(x, v as 'pilot' | 'live')); setHostToConfirm(null); }} />
            </Field>
            <Field label="UniGate URL" error={show('baseUrl') || (f.environment === 'live' && !f.baseUrl.trim() ? 'Enter the Live UniGate URL' : null)}
              hint={f.environment === 'pilot' ? 'Pilot URL from the integration docs.' : undefined}>
              <input className={`${INPUT} font-mono`} value={f.baseUrl} onChange={(e) => set('baseUrl', e.target.value)} placeholder="https://…" data-testid="ug-url" />
            </Field>
            {hostToConfirm && (
              <div role="alert" data-testid="ug-host-confirm" className="rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-[12px] text-amber-900">
                {hostToConfirm} is not a magensa.net host.
                <button type="button" onClick={() => submit(true)} disabled={busy}
                  className="ml-2 min-h-[36px] rounded-md px-2 font-semibold text-ruby hover:bg-[#FFF1F3]">Use it anyway</button>
              </div>
            )}
            {error && <p role="alert" className="text-[12px] text-ruby-deep">{error}</p>}
          </div>
        </div>
        {withTeconnect && (
          <section className="border-t border-stone-200 px-5 pb-5 pt-4" data-testid="te-section">
            <h3 className="m-0 text-[13px] font-semibold text-stone-900">Website (TEConnect)</h3>
            <p className="mb-3 mt-0.5 text-[11px] text-stone-500">{teconnectHint || 'Public checkout key. Leave UniGate empty above to save only this.'}</p>
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              <div className="md:col-span-2">
                <Field label="Public key" error={teProblem}>
                  <input className={`${INPUT} font-mono`} value={te.publicKey} onChange={(e) => setT('publicKey', e.target.value)} autoComplete="off" data-testid="te-key" />
                </Field>
              </div>
              <Field label="Google merchant id">
                <input className={`${INPUT} font-mono`} value={te.googleMerchantId} onChange={(e) => setT('googleMerchantId', e.target.value)} autoComplete="off" data-testid="te-gmid" />
              </Field>
              <Field label="Google gateway id">
                <input className={`${INPUT} font-mono`} value={te.googleGatewayId} onChange={(e) => setT('googleGatewayId', e.target.value)} autoComplete="off" data-testid="te-ggid" />
              </Field>
              <Field label="Website environment">
                <Segmented ariaLabel="Website environment" value={te.environment}
                  options={[{ id: 'sandbox', label: 'Sandbox' }, { id: 'production', label: 'Production' }]}
                  onChange={(v) => setT('environment', v)} />
              </Field>
            </div>
          </section>
        )}
        <footer className="flex items-center justify-end gap-2 border-t border-stone-200 px-5 py-3">
          <button type="button" onClick={onClose} className="min-h-[44px] rounded-lg px-4 text-sm font-medium text-stone-600 hover:bg-stone-100">Cancel</button>
          <button type="button" data-testid="ug-save" disabled={!canSave || busy} onClick={() => submit(false)}
            className="inline-flex min-h-[44px] items-center gap-2 rounded-lg bg-black px-5 text-sm font-semibold text-white hover:bg-stone-800 disabled:opacity-40">
            {busy && <RubyGemMark size={16} drawing color="currentColor" />} Save
          </button>
        </footer>
      </div>
    </div>
  );
};

/** A small house-style confirm (no native confirm). */
export const ConfirmLight: React.FC<{ title: string; message: string; confirmLabel: string; busy?: boolean; onConfirm: () => void; onCancel: () => void }> = ({ title, message, confirmLabel, busy, onConfirm, onCancel }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" role="dialog" aria-modal="true" aria-label={title}>
    <div className="w-full max-w-[440px] overflow-hidden rounded-2xl bg-white shadow-2xl" data-testid="confirm-light">
      <header className="bg-black px-5 py-3.5 text-[15px] font-semibold text-white">{title}</header>
      <div style={RUBY_UNDERLINE} />
      <p className="p-5 text-sm text-stone-700">{message}</p>
      <footer className="flex justify-end gap-2 border-t border-stone-200 px-5 py-3">
        <button type="button" onClick={onCancel} className="min-h-[44px] rounded-lg px-4 text-sm font-medium text-stone-600 hover:bg-stone-100">Cancel</button>
        <button type="button" onClick={onConfirm} disabled={busy} data-testid="confirm-light-ok"
          className="inline-flex min-h-[44px] items-center gap-2 rounded-lg bg-black px-5 text-sm font-semibold text-white hover:bg-stone-800 disabled:opacity-40">
          {busy && <RubyGemMark size={16} drawing color="currentColor" />}{confirmLabel}
        </button>
      </footer>
    </div>
  </div>
);
